import { IconName } from '@upsales/components';
import { useFeatureAvailable, useSelector, useSoftDeployAccess } from '../hooks';
import { Feature, getFeatureAvailableFromState, getSoftDeployAccessFromState } from '../hooks/featureHelper';
import { openDrawer } from 'Services/Drawer';
import openModal, { shouldOpenModal } from 'App/services/Modal';
import { openEditAppointment } from 'Components/Modals/Appointment/EditAppointment';
import { openEditCampaignModal } from 'App/components/EditCampaign/EditCampaign';
import { openEditOrder } from 'App/components/EditOrder';
import { openEditContact } from 'App/components/EditContact/openEditContact';
import type Client from 'App/resources/Model/Client';
import type Contact from 'App/resources/Model/Contact';
import { rankByContext } from './addActionsContext';

export type CreateActionContext = { type: 'company'; client?: Client } | { type: 'contact'; contact?: Contact };

export const useAddActions = (
	context?: CreateActionContext
): { title: string; type: string; show: boolean; icon?: IconName; do: () => void }[] => {
	const hasActivities = useFeatureAvailable(Feature.ACTIVITIES_AND_APPOINTMENTS);
	const hasTodoList = useSoftDeployAccess('TODO_LIST') && hasActivities;
	const { metadata, self, accessRights, admin, customerId, accountSelf } = useSelector(({ App }) => ({
		metadata: App.metadata,
		self: App.self,
		accessRights: App.accessRights,
		admin: App.self?.administrator ?? false,
		customerId: App.customerId,
		accountSelf: App.accountSelf
	}));

	const isCRMUser = self?.crm ?? true;

	const showEmail =
		useFeatureAvailable(Feature.EMAIL) &&
		(admin || metadata?.map.mailActivated || !!metadata?.integrations.inits.mail?.length || false);

	const showGroupMailAndMailTemplate =
		useFeatureAvailable(Feature.GROUP_MAIL) && (admin || metadata?.map.mailActivated || false);

	const hasCustomerSupport = useFeatureAvailable(Feature.CUSTOMER_SUPPORT);
	const showSupportTicket = !!self?.support && hasCustomerSupport;

	const hasProjectPlan = useSoftDeployAccess('PROJECT_PLAN');
	const hasProjectPlanFeature = useFeatureAvailable(Feature.PROJECT_PLAN);
	const requireServiceAddon = useSoftDeployAccess('SERVICE_MANAGEMENT_REQUIRE_ADDON');

	const showProjectPlan = !!(
		(hasProjectPlan || hasProjectPlanFeature) &&
		(requireServiceAddon ? self?.service : self?.service || self?.crm)
	);

	const showActivity =
		isCRMUser &&
		getFeatureAvailableFromState(accountSelf, Feature.ACTIVITIES_AND_APPOINTMENTS) &&
		!(getSoftDeployAccessFromState(accountSelf, 'REMOVE_ACTIVITIES') && hasTodoList);
	const showTodo = isCRMUser && hasTodoList;
	const showPhoneCall = isCRMUser && hasTodoList;
	const showAppointment = isCRMUser && getFeatureAvailableFromState(accountSelf, Feature.ACTIVITIES_AND_APPOINTMENTS);

	const companyClient = context?.type === 'company' ? context.client : undefined;
	const contact = context?.type === 'contact' ? context.contact : undefined;
	const clientId = companyClient?.id ?? contact?.client?.id;

	const actions: { title: string; type: string; show: boolean; icon?: IconName; do: () => void }[] = [
		{
			title: 'default.account',
			type: 'account',
			show: getFeatureAvailableFromState(accountSelf, Feature.COMPANIES_AND_CONTACTS),
			icon: 'building-o',
			do: () => {
				if (shouldOpenModal('CreateAccount')) {
					openModal('CreateAccount');
				} else {
					Tools.$upModal.open('createAccount', { customerId });
				}
			}
		},
		{
			title: 'default.contact',
			type: 'contact',
			show: getFeatureAvailableFromState(accountSelf, Feature.COMPANIES_AND_CONTACTS),
			icon: 'user',
			do: () => {
				openEditContact(companyClient ? { customerId, account: companyClient } : { customerId });
			}
		},
		{
			title: 'default.activity',
			type: 'activity',
			show: showActivity,
			icon: 'activity',
			do: () => {
				if (contact) {
					Tools.$upModal.open('editActivity', {
						activity: { contacts: [contact], ...(clientId ? { client: { id: clientId } } : {}) }
					});
				} else if (companyClient) {
					Tools.$upModal.open('editActivity', { activity: { client: { id: companyClient.id } } });
				} else {
					Tools.$upModal.open('editActivity', {});
				}
			}
		},
		{
			title: 'todo.createTodo',
			type: 'todo',
			show: showTodo,
			icon: 'activity',
			do: () => {
				if (contact) {
					openDrawer('CreateTodo', { contact });
				} else if (companyClient) {
					openDrawer('CreateTodo', { client: companyClient });
				} else {
					openDrawer('CreateTodo');
				}
			}
		},
		{
			title: 'todo.planACall',
			type: 'phonecall',
			show: showPhoneCall,
			icon: 'phone',
			do: () => {
				if (contact) {
					openDrawer('CreateCall', { contact });
				} else if (companyClient) {
					openDrawer('CreateCall', { client: companyClient });
				} else {
					openDrawer('CreateCall');
				}
			}
		},
		{
			title: 'todo.bookAppointment',
			type: 'appointment',
			show: showAppointment,
			icon: 'calendar',
			do: () => {
				if (contact) {
					openEditAppointment({
						appointment: {
							contacts: [{ id: contact.id }],
							...(clientId ? { client: { id: clientId } } : {})
						}
					});
				} else if (companyClient) {
					openEditAppointment({ appointment: { client: { id: companyClient.id } } });
				} else {
					openEditAppointment({});
				}
			}
		},
		{
			title: 'default.opportunity',
			type: 'opportunity',
			show: getFeatureAvailableFromState(accountSelf, Feature.PIPELINE),
			icon: 'opportunity',
			do: () => {
				openEditOrder({
					type: 'opportunity',
					...(clientId ? { clientId } : {}),
					...(contact ? { contactId: contact.id } : {})
				});
			}
		},
		{
			title: 'default.order',
			type: 'order',
			show: getFeatureAvailableFromState(accountSelf, Feature.ORDERS),
			icon: 'dollar',
			do: () => {
				openEditOrder({ type: 'order', ...(clientId ? { clientId } : {}) });
			}
		},
		{
			title: 'default.agreement',
			type: 'agreement',
			show:
				!!metadata?.params.AgreementEnabled &&
				getFeatureAvailableFromState(accountSelf, Feature.RECURRING_ORDER) &&
				accessRights.Agreement,
			icon: 'recurring-order',
			do: () => {
				if (getSoftDeployAccessFromState(accountSelf, 'SUBSCRIPTION_MODAL')) {
					openModal('CreateSubscription', {
						createdFrom: 'sidebar',
						dontWait: true,
						...(companyClient ? { client: companyClient } : {})
					});
				} else {
					Tools.$upModal.open(
						'editAgreement',
						companyClient ? { accountId: companyClient.id } : { customerId }
					);
				}
			}
		},
		{
			title: 'default.email',
			type: 'mail',
			show: showEmail,
			icon: 'envelope',
			do: () => {
				openDrawer('NewSingleMail');
			}
		},
		{
			title: 'default.mailinglist',
			type: 'mailCampaign',
			show: showGroupMailAndMailTemplate && (admin || self?.userParams.mailAdmin || false),
			icon: 'email-campaign',
			do: () => {
				openModal('CreateGroupMail');
			}
		},
		{
			title: 'mail.template',
			type: 'mailTemplate',
			show: showGroupMailAndMailTemplate,
			icon: 'envelope',
			do: () => {
				openModal('CreateMailTemplate');
			}
		},
		{
			title: 'default.campaign',
			type: 'campaign',
			show: getFeatureAvailableFromState(accountSelf, Feature.PROJECTS),
			icon: 'bullhorn',
			do: () => {
				openEditCampaignModal({ customerId });
			}
		},
		{
			title: 'form.form',
			type: 'form',
			show: getFeatureAvailableFromState(accountSelf, Feature.FORMS),
			icon: 'form',
			do: () => {
				openModal('CreateForm', { formType: 'form' });
			}
		},
		{
			title: 'form.landingPage',
			type: 'landingpage',
			show: getFeatureAvailableFromState(accountSelf, Feature.FORMS),
			icon: 'file-text',
			do: () => {
				openModal('CreateForm', { formType: 'landing' });
			}
		},
		{
			title: 'utm.generator',
			type: 'utm',
			show: getFeatureAvailableFromState(accountSelf, Feature.UTM_GENERATOR),
			icon: 'link',
			do: () => {
				openDrawer('UtmLinkGenerator');
			}
		},
		{
			title: 'ticket.createTicket',
			type: 'support',
			show: showSupportTicket,
			icon: 'customer-support',
			do: () => {
				openDrawer('EditTicket');
			}
		},
		{
			title: 'projectPlan.createProjectPlan',
			type: 'projectPlan',
			show: showProjectPlan,
			icon: 'project',
			do: () => {
				if (companyClient) {
					openDrawer('EditProjectPlan', { client: companyClient });
				} else {
					openDrawer('EditProjectPlan');
				}
			}
		}
	];

	return rankByContext(actions, context);
};
